import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Calendar, Clock, RefreshCw, Trash2, X } from 'lucide-react';
import { format } from 'date-fns';

interface ScheduledRollover {
  id: string;
  entity_types: string[];
  old_season_name: string;
  new_season_name: string;
  scheduled_for: string;
  notes: string | null;
  execution_status: string;
  executed_at: string | null;
  execution_error: string | null;
  send_notifications: boolean;
  created_at: string;
}

export const ScheduledRolloverManager = () => {
  const { user } = useAuth();
  const [scheduledRollovers, setScheduledRollovers] = useState<ScheduledRollover[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);

  // Form state
  const [entityTypes, setEntityTypes] = useState<string[]>([]);
  const [oldSeasonName, setOldSeasonName] = useState('');
  const [newSeasonName, setNewSeasonName] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [notes, setNotes] = useState('');
  const [sendNotifications, setSendNotifications] = useState(true);

  useEffect(() => {
    loadScheduledRollovers();
  }, []);

  const loadScheduledRollovers = async () => {
    try {
      const { data, error } = await supabase
        .from('scheduled_season_rollovers')
        .select('*')
        .order('scheduled_for', { ascending: true });

      if (error) throw error;
      setScheduledRollovers(data || []);
    } catch (error) {
      console.error('Error loading scheduled rollovers:', error);
      toast.error('Failed to load scheduled rollovers');
    }
  };

  const handleEntityTypeToggle = (type: string) => {
    setEntityTypes(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  const handleScheduleRollover = async () => {
    if (!user?.id || entityTypes.length === 0 || !oldSeasonName || !newSeasonName || !scheduledDate || !scheduledTime) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const scheduledFor = new Date(`${scheduledDate}T${scheduledTime}`).toISOString();

      const { error } = await supabase
        .from('scheduled_season_rollovers')
        .insert({
          entity_types: entityTypes,
          old_season_name: oldSeasonName,
          new_season_name: newSeasonName,
          scheduled_for: scheduledFor,
          scheduled_by: user.id,
          scheduled_by_type: 'supabase',
          notes: notes || null,
          send_notifications: sendNotifications,
        });

      if (error) throw error;

      toast.success('Season rollover scheduled successfully!');
      setShowForm(false);
      resetForm();
      loadScheduledRollovers();
    } catch (error) {
      console.error('Error scheduling rollover:', error);
      toast.error('Failed to schedule season rollover');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRollover = async (id: string) => {
    try {
      const { error } = await supabase
        .from('scheduled_season_rollovers')
        .update({ execution_status: 'cancelled' })
        .eq('id', id);

      if (error) throw error;

      toast.success('Scheduled rollover cancelled');
      loadScheduledRollovers();
    } catch (error) {
      console.error('Error cancelling rollover:', error);
      toast.error('Failed to cancel rollover');
    }
  };

  const handleDeleteRollover = async (id: string) => {
    try {
      const { error } = await supabase
        .from('scheduled_season_rollovers')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast.success('Scheduled rollover deleted');
      loadScheduledRollovers();
    } catch (error) {
      console.error('Error deleting rollover:', error);
      toast.error('Failed to delete rollover');
    }
  };

  const resetForm = () => {
    setEntityTypes([]);
    setOldSeasonName('');
    setNewSeasonName('');
    setScheduledDate('');
    setScheduledTime('');
    setNotes('');
    setSendNotifications(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'text-yellow-500';
      case 'in_progress': return 'text-blue-500';
      case 'completed': return 'text-green-500';
      case 'failed': return 'text-red-500';
      case 'cancelled': return 'text-gray-500';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Scheduled Season Rollovers</CardTitle>
              <CardDescription>
                Schedule season rollovers to execute automatically at specified dates and times
              </CardDescription>
            </div>
            <Button onClick={() => setShowForm(!showForm)}>
              {showForm ? <X className="w-4 h-4 mr-2" /> : <Calendar className="w-4 h-4 mr-2" />}
              {showForm ? 'Cancel' : 'Schedule New Rollover'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {showForm && (
            <div className="mb-6 p-4 border rounded-lg space-y-4">
              <div>
                <Label>Entity Types *</Label>
                <div className="flex gap-4 mt-2">
                  {['coach', 'team_owner', 'venue_owner'].map(type => (
                    <div key={type} className="flex items-center space-x-2">
                      <Checkbox
                        id={type}
                        checked={entityTypes.includes(type)}
                        onCheckedChange={() => handleEntityTypeToggle(type)}
                      />
                      <label htmlFor={type} className="text-sm font-medium capitalize cursor-pointer">
                        {type.replace('_', ' ')}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="oldSeason">Current Season Name *</Label>
                  <Input
                    id="oldSeason"
                    value={oldSeasonName}
                    onChange={(e) => setOldSeasonName(e.target.value)}
                    placeholder="e.g., 2024 Season"
                  />
                </div>
                <div>
                  <Label htmlFor="newSeason">New Season Name *</Label>
                  <Input
                    id="newSeason"
                    value={newSeasonName}
                    onChange={(e) => setNewSeasonName(e.target.value)}
                    placeholder="e.g., 2025 Season"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="date">Scheduled Date *</Label>
                  <Input
                    id="date"
                    type="date"
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="time">Scheduled Time *</Label>
                  <Input
                    id="time"
                    type="time"
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any notes about this rollover..."
                  rows={3}
                />
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="notifications"
                  checked={sendNotifications}
                  onCheckedChange={(checked) => setSendNotifications(checked as boolean)}
                />
                <label htmlFor="notifications" className="text-sm font-medium cursor-pointer">
                  Send email notifications when executed
                </label>
              </div>

              <Button onClick={handleScheduleRollover} disabled={loading} className="w-full">
                <Calendar className="w-4 h-4 mr-2" />
                Schedule Rollover
              </Button>
            </div>
          )}

          <div className="space-y-4">
            {scheduledRollovers.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                No scheduled rollovers found
              </p>
            ) : (
              scheduledRollovers.map((rollover) => (
                <div
                  key={rollover.id}
                  className="p-4 border rounded-lg space-y-3"
                >
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold">
                          {rollover.old_season_name} → {rollover.new_season_name}
                        </h4>
                        <span className={`text-sm font-medium capitalize ${getStatusColor(rollover.execution_status)}`}>
                          {rollover.execution_status}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Entity Types: {rollover.entity_types.map(t => t.replace('_', ' ')).join(', ')}
                      </p>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {format(new Date(rollover.scheduled_for), 'MMM dd, yyyy')}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {format(new Date(rollover.scheduled_for), 'hh:mm a')}
                        </span>
                      </div>
                      {rollover.notes && (
                        <p className="text-sm text-muted-foreground mt-2">{rollover.notes}</p>
                      )}
                      {rollover.execution_error && (
                        <p className="text-sm text-red-500 mt-2">Error: {rollover.execution_error}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {rollover.execution_status === 'pending' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleCancelRollover(rollover.id)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                      {['completed', 'failed', 'cancelled'].includes(rollover.execution_status) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteRollover(rollover.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
